export function dateFromDateOrString(
  d: Date | string | null | undefined
): Date | undefined {
  let date: Date | undefined = undefined;
  if (typeof d === 'string') {
    date = new Date(d);
  } else {
    date = d ?? undefined;
  }
  if (date && !isNaN(date.getTime())) {
    return date;
  } else {
    return undefined;
  }
}

/**
 * Given a string or a falsy value, returns a string of the date in the
 * preferred UI format, or an empty string if value passed could not be parsed
 * into a date.
 * @param {Date | null | string} d Value to turn into a date string
 */
export function dateToFormattedString(d: Date | null | string) {
  const date = dateFromDateOrString(d);
  if (date) {
    const yyyy = date.getFullYear().toString();
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');
    return `${mm}/${dd}/${yyyy}`;
  } else {
    return '';
  }
}

export function dateToYyyyMmDdString(d: Date | null | string) {
  const date = dateFromDateOrString(d);
  if (date) {
    const yyyy = date.getFullYear().toString();
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } else {
    return '';
  }
}

export function dateTimeToFormattedString(d: Date | null | string) {
  const date = dateFromDateOrString(d);
  if (date) {
    const yyyy = date.getFullYear().toString();
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const dd = date.getDate().toString().padStart(2, '0');
    const time = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    return `${mm}/${dd}/${yyyy} ${time}`;
  } else {
    return '';
  }
}

export function dateTimeToTimeString(d: Date | null | string) {
  const date = dateFromDateOrString(d);
  if (date) {
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  } else {
    return '';
  }
}

export function dateStringToServerDateString(
  d: Date | string | null | undefined
): string | undefined {
  const date = dateFromDateOrString(d);

  return date === undefined ? undefined : date.toISOString();
}

/**
 * Example: '2000-10-31T01:30:00.000-05:00' => '200010'
 * @param {string} s string in ISO DateTime format
 */
export function isoDateTimeStringToYyyyMm(s: string) {
  return s.substring(0, 4) + s.substring(5, 7);
}

// If date string specifies a date at exactly midnight UTC, returns a date object at midnight
// in the local timezone on the given date. Examples:
// '2024-10-31T00:00:00.000Z' will return a date object like:
// Oct 31 2024 00:00:00 GMT-0400 (Eastern Daylight Time)
// '2024-10-31T00:00:01.000Z', will receive no special treatment, and returns a date object like:
// Oct 30 2024 20:00:01 GMT-0400 (Eastern Daylight Time)

export function dateStringToDateMaybeIgnoringTimeZone(s: string) {
  const d = new Date(s);
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  ) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  } else {
    return d;
  }
}

/**
 * Example '200010' => { year: 2000, month: 10 }
 * @param {string} s YYYYMM string
 */
export function yyyyMmToYearMonth(s: string) {
  return {
    year: parseInt(s.substring(0, 4)),
    month: parseInt(s.substring(4, 6)),
  };
}

/**
 * Examples: '200012', 2 => '200102', '200012', -2 => '200010'
 * @param {string} s           YYYYMM string
 * @param {number} monthsToAdd # of months to add or substract
 */
export function addMonthsToYyyyMm(s: string, monthsToAdd: number) {
  let { year, month } = yyyyMmToYearMonth(s);
  month = month + monthsToAdd;

  // months are 1-based so let's not get fancy with the math
  while (month > 12) {
    month -= 12;
    year += 1;
  }
  while (month < 1) {
    month += 12;
    year -= 1;
  }
  return `${year.toString().padStart(4, '0')}${month
    .toString()
    .padStart(2, '0')}`;
}

const shortMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const longMonths = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Example: '200010' => 'Oct 2000'
 * @param {string} s               YYYYMM string
 * @param {boolean} names = true   Use month names instead of numbers
 * @param {boolean} short = true   Use short month names instead of long
 */
export function yyyyMmToFormattedString(s: string, names = true, short = true) {
  const { year, month } = yyyyMmToYearMonth(s);
  const m = names
    ? (short ? shortMonths[month - 1] : longMonths[month - 1]) + ' '
    : month + '/';
  return `${m}${year}`;
}

/**
 * Example: '2000-10-01T00:00:00.000Z' => 'Oct 2000'
 * @param {string} s               ISO DateTime string
 * @param {boolean} names = true   Use month names instead of numbers
 * @param {boolean} short = true   Use short month names instead of long
 */
export function isoDateTimeStringToYearMonthString(
  s: string,
  names = true,
  short = true
) {
  return yyyyMmToFormattedString(isoDateTimeStringToYyyyMm(s), names, short);
}

export function getMidnightOfDate(d: Date): Date {
  const midnight = new Date(d);
  midnight.setHours(0, 0, 0, 0);
  return midnight;
}

export function getMidnightToday(): Date {
  return getMidnightOfDate(new Date());
}

export function getMidnightTomorrow(): Date {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return getMidnightOfDate(tomorrow);
}

export function getOneSecondBeforeMidnightTomorrow(): Date {
  const tomorrow = getMidnightTomorrow();
  tomorrow.setSeconds(tomorrow.getSeconds() - 1);
  return tomorrow;
}

type DatePreference = 'future' | 'past' | undefined;

type dateOnlyParserOptions = {
  minDate?: {
    fuzzy?: Date;
    firm?: Date;
  };
  maxDate?: {
    fuzzy?: Date;
    firm?: Date;
  };
  pattern?: string;
  preference?: {
    direction?: DatePreference;
  };
};

export function guessYear(
  year2Digit: number | undefined,
  month: number,
  day: number,
  options?: dateOnlyParserOptions
): number {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentYear2Digit = currentYear % 100;
  const currentCentury = Math.floor(currentYear / 100) * 100;

  // Handle year2Digit if provided
  if (year2Digit) {
    const candidate = currentCentury + year2Digit;
    if (year2Digit > currentYear2Digit) {
      const prevCentury = currentCentury - 100;
      const candidateDate = new Date(candidate, month - 1, day);
      const altDate = new Date(prevCentury + year2Digit, month - 1, day);
      const diff = Math.abs(candidateDate.getTime() - now.getTime());
      const altDiff = Math.abs(altDate.getTime() - now.getTime());

      // Prefer based on the provided direction (past/future)
      if (options?.preference?.direction === 'past' && altDate < now) {
        return prevCentury + year2Digit;
      } else if (
        options?.preference?.direction === 'future' &&
        candidateDate > now
      ) {
        return candidate;
      }

      // Return whichever is closer
      return diff <= altDiff ? candidate : prevCentury + year2Digit;
    }
    return candidate;
  }

  // Handle month/day logic if year2Digit is not provided
  const targetDate = new Date(currentYear, month - 1, day);
  const diffDays =
    (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

  // Respect preference direction if provided
  if (options?.preference?.direction === 'future') {
    return diffDays >= 0 ? currentYear : currentYear + 1;
  } else if (options?.preference?.direction === 'past') {
    return diffDays <= 0 ? currentYear : currentYear - 1;
  }

  // Default logic: return the closest year (threshold of 183 days)
  return Math.abs(diffDays) <= 183
    ? currentYear
    : diffDays > 0
      ? currentYear + 1
      : currentYear - 1;
}

export function formYear(
  century2Digit: number, // First 2 Digits of the Year (ex 19xx or 20xx)
  year2Digit: number // Last 2 Digits of the Year (ex xx01 or xx88)
): number {
  return century2Digit * 100 + year2Digit;
}

function findFirstValidDate(
  candidates: {
    date: Date;
    year: number;
    month: number;
    day: number;
  }[],
  options?: dateOnlyParserOptions
): {
    year: number;
    month: number;
    day: number;
  } {
  const now = new Date();
  const dateParts: {
    month: number;
    day: number;
    year: number;
  } = {
    month: now.getMonth() + 1, // JS months are 0-indexed
    day: now.getDate(),
    year: now.getFullYear(),
  };

  let primaryCount = 0;
  const validCandidates = candidates.reduce<
    { year: number; month: number; day: number }[]
  >((acc, { date, year, month, day }) => {
    const sameDay =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (
      !sameDay ||
      (options?.minDate?.firm && date < options.minDate.firm) ||
      (options?.maxDate?.firm && date > options.maxDate.firm)
    ) {
      return acc; // invalid
    }
    const violatesPreference =
      (options?.preference?.direction === 'future' && date < now) ||
      (options?.preference?.direction === 'past' && date > now);

    const isOutsideFuzzyBounds =
      (options?.minDate?.fuzzy && date < options.minDate.fuzzy) ||
      (options?.maxDate?.fuzzy && date > options.maxDate.fuzzy);

    const isDeprioritized = violatesPreference || isOutsideFuzzyBounds;

    const candidate = { year, month, day };

    // If fuzzy bounds are violated or preference is violated => secondary
    if (isDeprioritized) {
      acc.push(candidate); // secondary
    } else {
      acc.splice(primaryCount, 0, candidate); // insert into primary section
      primaryCount++;
    }
    return acc;
  }, []);

  const best = validCandidates[0];
  if (best) {
    dateParts.year = best.year;
    dateParts.month = best.month;
    dateParts.day = best.day;
  }

  return dateParts;
}

export function parseYearMonthDayFrom4Chunks(
  chunkA: number,
  chunkB: number,
  chunkC: number,
  chunkD: number,
  options?: dateOnlyParserOptions
): {
    year: number;
    month: number;
    day: number;
  } {
  // Create candidate dates based on different formats
  const candidates = [
    {
      date: new Date(formYear(chunkC, chunkD), chunkA - 1, chunkB),
      year: formYear(chunkC, chunkD),
      month: chunkA,
      day: chunkB,
      pattern: 'MMDDYYYY',
    },
    {
      date: new Date(formYear(chunkA, chunkB), chunkC - 1, chunkD),
      year: formYear(chunkA, chunkB),
      month: chunkC,
      day: chunkD,
      pattern: 'YYYYMMDD',
    },
    {
      date: new Date(formYear(chunkC, chunkD), chunkB - 1, chunkA),
      year: formYear(chunkC, chunkD),
      month: chunkB,
      day: chunkA,
      pattern: 'DDMMYYYY',
    },
    {
      date: new Date(formYear(chunkA, chunkB), chunkD - 1, chunkC),
      year: formYear(chunkA, chunkB),
      month: chunkD,
      day: chunkC,
      pattern: 'YYYYDDMM',
    },
  ];

  return findFirstValidDate(candidates, options);
}

export function parseYearMonthDayFrom3Chunks(
  chunkA: number,
  chunkB: number,
  chunkC: number,
  options?: dateOnlyParserOptions
): {
    year: number;
    month: number;
    day: number;
  } {
  // Create candidate dates based on different formats
  const candidates = [
    {
      year: guessYear(chunkC, chunkA - 1, chunkB, options),
      month: chunkA,
      day: chunkB,
      pattern: 'MMDDYY',
    },
    {
      year: guessYear(chunkA, chunkB - 1, chunkC, options),
      month: chunkB,
      day: chunkC,
      pattern: 'YYMMDD',
    },
    {
      year: guessYear(chunkC, chunkB - 1, chunkA, options),
      month: chunkB,
      day: chunkA,
      pattern: 'DDMMYY',
    },
    {
      year: guessYear(chunkA, chunkC - 1, chunkB, options),
      month: chunkC,
      day: chunkB,
      pattern: 'YYDDMM',
    },
  ].map((candidate) => ({
    ...candidate,
    date: new Date(candidate.year, candidate.month - 1, candidate.day),
  }));

  return findFirstValidDate(candidates, options);
}

function parseYearMonthDayFrom2Chunks(
  chunkA: number,
  chunkB: number,
  options?: dateOnlyParserOptions
): {
    month: number;
    day: number;
    year: number;
  } {
  // Create candidate dates based on different formats
  const candidates = [
    // MMDD format
    {
      year: guessYear(undefined, chunkA - 1, chunkB, options),
      month: chunkA,
      day: chunkB,
      pattern: 'MMDD',
    },
    // DDMM format
    {
      year: guessYear(undefined, chunkB - 1, chunkA, options),
      month: chunkB,
      day: chunkA,
      pattern: 'DDMM',
    },
  ].map((candidate) => ({
    ...candidate,
    date: new Date(candidate.year, candidate.month - 1, candidate.day),
  }));
  return findFirstValidDate(candidates, options);
}

export function dateOnlyParser(
  dateString: string,
  options?: dateOnlyParserOptions
): Date {
  const resolvedDate = new Date();
  let month = resolvedDate.getMonth() + 1;
  let day = resolvedDate.getDate();
  let year = resolvedDate.getFullYear();
  const digits = dateString.match(/\d/g) || [];
  if (digits.length === 8) {
    // Given 8 digits
    ({ month, day, year } = parseYearMonthDayFrom4Chunks(
      parseInt(digits.slice(0, 2).join(''), 10),
      parseInt(digits.slice(2, 4).join(''), 10),
      parseInt(digits.slice(4, 6).join(''), 10),
      parseInt(digits.slice(6, 8).join(''), 10),
      options
    ));
  } else if (digits.length >= 6) {
    // Given 6 digits
    ({ month, day, year } = parseYearMonthDayFrom3Chunks(
      parseInt(digits.slice(0, 2).join(''), 10),
      parseInt(digits.slice(2, 4).join(''), 10),
      parseInt(digits.slice(4, 6).join(''), 10),
      options
    ));
  } else if (digits.length >= 4) {
    // Given 4 digits
    ({ month, day, year } = parseYearMonthDayFrom2Chunks(
      parseInt(digits.slice(0, 2).join(''), 10),
      parseInt(digits.slice(2, 4).join(''), 10),
      options
    ));
  }
  resolvedDate.setFullYear(year);
  resolvedDate.setMonth(month - 1, day); // JS months are 0-indexed
  return resolvedDate;
}
